import React from "react";
import { useInView } from "react-intersection-observer";
import { defineMessage, useIntl } from "react-intl";
import { ScatterplotAuto } from "../charts-motion/scatterplot";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_fig_GOVVGDP";
import { datasets } from "../data/data_finance_fig_GOVVGDP_datasets";
import { countryIdIso, getCountryMeta, getRegionName, mkGemEntityDecoder, mkOrdRegion, regionIdIso, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities, useTimelineState } from "../domain";
import { useMedia } from "../hooks";
import { catOptions, extentNumber, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_finance_fig_GOVVGDP";
const DEFAULT_COUNTRIES = [
    "CMR",
    "BRA",
    "ISL",
    "BDI"
];
export const Data = mkGemEntityDecoder([
    "year"
], {
    "EDU.SHARE.GDP": io.number,
    "EDU.SHARE.GOV": io.number
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const env = useConfig();
    const [, actions] = useFigureState();
    const [selectedYear] = useTimelineState("year");
    const [selectedCountries] = useMultiSelectState("countries");
    const [ref, inView] = useInView({
    });
    const entityData = useNamedEntities(dataRaw);
    const countryData_ = useCountryEntities(entityData);
    const countryData = React.useMemo(()=>{
        const data = countryData_.map((x)=>{
            return pipe(getCountryMeta(env.countries, x.id), O.chain((meta)=>pipe(O.fromNullable(meta.income), O.fold(()=>O.none, (income)=>O.some({
                        ...meta,
                        income
                    })))), O.map((meta)=>({
                    ...x,
                    region: meta.income,
                    region_name: getRegionName(env.regions, meta.income)
                })));
        });
        return pipe(data, catOptions, Ar.sortBy([
            mkOrdRegion(env.regions, (x)=>x.region),
            Ord.contramap((x)=>x.year)(Ord.ordNumber)
        ]));
    }, [
        env.countries,
        env.regions,
        countryData_
    ]);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    const yearExtent = React.useMemo(()=>extentNumber(unsafeFromArray(countryData), (x)=>x.year), [
        countryData
    ]);
    React.useEffect(()=>{
        actions.setToggleControl("year", {
            type: "Timeline",
            range: yearExtent,
            selected: yearExtent[1],
            checked: inView
        });
    }, [
        inView,
        yearExtent,
        actions
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            minItems: 0,
            maxItems: 5,
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
        actions.setToggleControl("year", {
            type: "Timeline",
            range: yearExtent,
            selected: yearExtent[1],
            checked: false
        });
    }, [
        intl,
        actions,
        countryControlItems,
        yearExtent
    ]);
    const chartData = unsafeFromArray(countryData);
    const getX = React.useCallback((x)=>x?.["EDU.SHARE.GDP"], []);
    const getY = React.useCallback((x)=>x?.["EDU.SHARE.GOV"], []);
    const getColor = React.useCallback((x)=>regionIdIso.unwrap(x.region), []);
    const getGroup = React.useCallback((x)=>countryIdIso.unwrap(x.id), []);
    const formatColor = React.useCallback((x)=>x.region_name, []);
    const formatGroup = React.useCallback((x)=>x.entity_name, []);
    const annotations = React.useMemo(()=>({
            x: [
                0.04,
                0.06
            ],
            y: [
                0.15,
                0.2
            ],
            labels: [
                {
                    label: intl.formatMessage(defineMessage({
                        id: "Benchmark",
                        defaultMessage: "Benchmark"
                    })),
                    x: 0.04,
                    y: 0.2,
                    dx: "0.4em",
                    dy: "1.2em"
                }
            ]
        }), [
        intl
    ]);
    const xTicks = React.useMemo(()=>[
            0,
            0.09
        ], []);
    const yTicks = React.useMemo(()=>[
            0,
            0.3
        ], []);
    return <div ref={ref}>
      <ScatterplotAuto height={screenSDown ? M.chartHeight.s : screenMDown ? M.chartHeight.m : M.chartHeight.l} data={chartData} getX={getX} getY={getY} getColor={getColor} getGroup={getGroup} xLabel={intl.formatMessage(defineMessage({
        id: "EDU.SHARE.GDP",
        defaultMessage: "Share of GDP"
    }))} yLabel={intl.formatMessage(defineMessage({
        id: "EDU.SHARE.GOV",
        defaultMessage: "Share of government expenditure"
    }))} formatColor={formatColor} formatGroup={formatGroup} xTicks={xTicks} yTicks={yTicks} selectedYear={selectedYear} highlightedGroups={selectedCountries} annotations={annotations} onSelect={(x)=>actions.toggleMultiSelectItem("countries", countryIdIso.unwrap(x.id))}/>
    </div>;
};
export default withFigureIO({
    url: new URL("../data/data_finance_fig_GOVVGDP.json", import.meta.url).href,
    csv: new URL("../data/data_finance_fig_GOVVGDP.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_fig_GOVVGDP.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
